import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowDown, Archive } from "lucide-react";
import { api, errMsg, fmtSize, triggerDownload } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLiveStats } from "../context/LiveStats";
import { LiveNumber } from "./LiveNumber";
import { SectionTitle } from "./Reveal";
import { useT } from "../lib/i18n";

const SPANS = ["lg:col-span-2 lg:row-span-2", "lg:col-span-1", "lg:col-span-1", "lg:col-span-2"];
const TEX = "/brand/tools.webp";

const ToolCard = ({ tool, idx, onDownload }) => {
  const big = idx % 4 === 0;
  const { t } = useT();
  return (
    <motion.div
      data-testid={`tool-card-${idx + 1}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: (idx % 4) * 0.08 }}
      className={`group relative overflow-hidden border border-white/10 bg-[#0A1424] hover:border-[#1FD1FF]/50 transition-colors p-6 flex flex-col justify-between min-h-[220px] ${SPANS[idx % 4]}`}
    >
      {big && (
        <>
          <img src={TEX} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:scale-105 transition-transform duration-[1.2s]" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A1424] via-[#0A1424]/70 to-transparent" />
        </>
      )}
      <div className="relative flex items-start justify-between gap-4">
        <span className="font-mono text-[11px] uppercase tracking-widest text-[#1FD1FF] border border-[#1FD1FF]/40 px-2 py-1">{tool.ext} · {fmtSize(tool.size)}</span>
        <Archive className="w-5 h-5 text-[#A8B3C4]" />
      </div>
      <div className="relative">
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#A8B3C4] mb-2">{tool.category}</p>
        <h3 className={`font-display leading-[0.9] ${big ? "text-6xl sm:text-7xl" : "text-4xl"}`}>{tool.title}</h3>
        {tool.description && <p className={`text-sm text-[#A8B3C4] mt-3 max-w-md ${big ? "" : "line-clamp-2"}`}>{tool.description}</p>}
        <div className="mt-5 flex items-center justify-between">
          <span data-testid={`tool-downloads-${idx + 1}`} className="font-mono text-xs text-[#A8B3C4]"><LiveNumber value={tool.downloads} /> {t("tools.downloads")}</span>
          <button data-testid={`tool-download-button-${idx + 1}`} onClick={() => onDownload(tool)}
            className="flex items-center gap-2 px-4 py-2.5 bg-white text-black font-mono text-xs uppercase tracking-widest hover:bg-[#1FD1FF] transition-colors">
            {t("tools.download")} <ArrowDown className="w-3.5 h-3.5 group-hover:translate-y-0.5 transition-transform" />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export const ToolsSection = () => {
  const { requireUser } = useAuth();
  const { counts, refresh } = useLiveStats();
  const [tools, setTools] = useState([]);
  const { t } = useT();

  useEffect(() => {
    api.get("/tools").then((r) => setTools(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const onDownload = async (tool) => {
    if (!requireUser()) return;
    try {
      const r = await api.post(`/tools/${tool.id}/download`);
      setTools((ts) => ts.map((x) => (x.id === tool.id ? { ...x, downloads: r.data.downloads } : x)));
      refresh();
      triggerDownload(r.data.url);
      toast.success(`${t("toast.downloading")} ${tool.title}`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <section id="tools" data-testid="tools-section" className="max-w-[1500px] mx-auto px-4 lg:px-10 py-24 lg:py-36">
      <SectionTitle index="02" kicker={t("tools.kicker")} title={<>{t("tools.title")}<span className="text-[#1FD1FF]">.</span></>}>
        <p className="max-w-sm text-[#A8B3C4]">{t("tools.body")}</p>
      </SectionTitle>
      {tools.length === 0 ? (
        <p className="font-mono text-sm text-[#A8B3C4] py-16 text-center border border-dashed border-white/10">{t("tools.empty")}</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 lg:auto-rows-[240px] grid-flow-dense gap-4">
          {tools.map((x, i) => <ToolCard key={x.id} tool={{ ...x, ...(counts.tools[x.id] || {}) }} idx={i} onDownload={onDownload} />)}
        </div>
      )}
    </section>
  );
};
